/**
 * Days as the attorney reads them, all through the platform's own formatter
 * and in the attorney's own time zone. One place, so the cards, the deleted
 * rows and the date fields agree on what day it is.
 */

/**
 * The month is asked of `en-US` because its short September is "Sep" in every
 * engine, where the British one is "Sept" in some; the parts are then laid out
 * in the British order the design writes.
 */
const SHOWN = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

/** Two-digit parts, for the `YYYY-MM-DD` a date field reads and writes. */
const FIELD = new Intl.DateTimeFormat("en-CA", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function partsOf(format: Intl.DateTimeFormat, moment: Date) {
  const parts = format.formatToParts(moment);
  return (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
}

/** A served time as the day it fell on: "23 Sep 2026". */
export function dayOf(served: string): string {
  const part = partsOf(SHOWN, new Date(served));
  return `${part("day")} ${part("month")} ${part("year")}`;
}

/** A calendar day as the server keeps a deadline, "2026-10-03", read as that day wherever the attorney is. */
export function dayOfDate(served: string): string {
  // A time with no zone is read as the attorney's own, so the day stays the day.
  return dayOf(`${served}T00:00:00`);
}

/** Today, as a date field writes it. */
export function localToday(): string {
  const part = partsOf(FIELD, new Date());
  return `${part("year")}-${part("month")}-${part("day")}`;
}

const CLOCK = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/**
 * A served time as the moment something happened, for a list of recent acts:
 * "today at 17:51", or "25 Sep at 10:00" on any other day.
 */
export function momentOf(served: string, now: Date = new Date()): string {
  const moment = new Date(served);
  const day = partsOf(SHOWN, moment);
  const today = FIELD.format(moment) === FIELD.format(now);
  const when = today ? "today" : `${day("day")} ${day("month")}`;
  return `${when} at ${CLOCK.format(moment)}`;
}

/** A served time as the day and the time on the clock: "7 Oct 2026, 14:05", for a record of who did what when. */
export function stampOf(served: string): string {
  return `${dayOf(served)}, ${CLOCK.format(new Date(served))}`;
}
